import { SwipeableDrawer } from '@mui/material';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import { useRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import ChatToVoice from '../assets/icons/ChatToVoice';
import pecty from '../assets/icons/VoiceConversation/chat.png';
import Editor from '../components/Editor';
import Header from '../components/Header';
import ItineraryPopUp from '../components/ItineraryPopUp';
import { ChatMessage } from '../lib/types';
import {
  imageUrlsState,
  itineraryState,
  sendExternalState,
  sessionIdState,
  topicsState,
  videoUrlsState,
} from '../recoil';
import { agentState } from '../recoil/agent/atom';
import { chatHistoryAtom } from '../recoil/chatHistory/atom';
import { chatState } from '../recoil/chatState/atom';
import { sendMessageToAuroraStreaming } from '../services/api';
import { isMobile } from 'react-device-detect';
import { initializeChatHistory, useQuery } from '../services/utils';

const ChatComponent = () => {
  const [userInput, setUserInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const messageArrayRef = useRef<any[]>([]);
  const [sendExternal, setSendExternal] = useRecoilState(sendExternalState);

  const [itinerary, setItinerary] = useRecoilState(itineraryState);
  const [sessionId, setSessionId] = useRecoilState(sessionIdState);
  const [websocketId, setWebsocketId] = useState('');
  const [isItineraryOpen, setIsItineraryOpen] = useState(false);
  const [topics, setTopics] = useRecoilState(topicsState);

  const [imageUrls, setImageUrls] = useRecoilState(imageUrlsState);
  const [videoUrls, setvideoUrls] = useRecoilState(videoUrlsState);

  const [currentAgent, setCurrentAgent] = useRecoilState(agentState);
  const [hasBeenInChat, setHasBeenInChat] = useRecoilState(chatState);

  const itineraryMessage: ChatMessage = {
    entity: 'ai',
    content: (
      <div className="ItineraryMessage flex-col items-start">
        <div className=" mb-2 text-[1rem] font-semibold">Your Itinerary</div>
        <div className="mb-4 text-[14px]">
          Click the button below to view your itinerary!
        </div>
        <div
          onClick={() => setDrawerShowing(true)}
          className=" w-min cursor-pointer rounded-lg border-[1px] border-[#5570F1] px-6 py-2 font-normal text-[#5570F1]"
        >
          View
        </div>
      </div>
    ),
    HideIcon: true,
  };

  const [chatHistory, setChatHistory] =
    useRecoilState<ChatMessage[]>(chatHistoryAtom);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const [isDrawerShowing, setDrawerShowing] = useState(false);

  const WEBSOCKET_URL =
    process.env.REACT_APP_WEBSOCKET_URL ||
    'wss://s8d4gka559.execute-api.us-east-1.amazonaws.com/Prod';

  useEffect(() => {
    console.log('CHAT HISTORY: ', chatHistory);
    if (chatHistory.length >= 2) {
      setHasBeenInChat(true);
    }
  }, [chatHistory]);

  useEffect(() => {
    let sessionId = uuidv4();
    setSessionId(sessionId);
    console.log(`New sessionId: ${sessionId}`);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | null) => {
    console.log('handling Sumbit');
    if (e) {
      e.preventDefault();
    }

    const message = userInput;

    if (isLoading) {
      console.log('loading');
      return;
    }

    if (message.trim() === '') {
      return; // Prevent submission of empty messages
    }

    setIsLoading(true);
    // setChatHistory([...chatHistory, { entity: 'human', content: message }]);
    setUserInput('');
    await handleBot(message);
  };

  const handlePresetPromptClick = async (text: string) => {
    setIsLoading(true);
    // setChatHistory([...chatHistory, { entity: 'human', content: text }]);
    await handleBot(text);
  };
  const handleBot = async (userMessage: string) => {
    setIsLoading(true);

    // Ensure user's message is added first and maintain order
    setChatHistory((prevHistory) => {
      if (
        prevHistory.length === 0 ||
        prevHistory[prevHistory.length - 1].entity === 'ai'
      ) {
        return [
          ...prevHistory,
          { entity: 'human' as const, content: userMessage },
        ];
      }
      return prevHistory; // Prevent duplicate user messages
    });

    // Process AI response
    let streamingBuffer = '';
    await sendMessageToAuroraStreaming(
      userMessage,
      sessionId,
      currentAgent || 'spark',
      imageUrls,
      videoUrls,
      (partialMessage, isFinal) => {
        if (!isFinal) {
          streamingBuffer += partialMessage;
        }
        setChatHistory((prevHistory) => {
          if (prevHistory.length === 0) return prevHistory;

          const lastMessage = prevHistory[prevHistory.length - 1];

          if (lastMessage.entity === 'ai') {
            // Update last AI message with streaming text
            return prevHistory.map((msg, index) =>
              index === prevHistory.length - 1
                ? { ...msg, content: streamingBuffer }
                : msg
            );
          } else if (lastMessage.entity === 'human') {
            // Only add a new AI message if the last one was from a human
            return [
              ...prevHistory,
              { entity: 'ai' as const, content: streamingBuffer },
            ];
          }

          return prevHistory;
        });

        if (isFinal) setIsLoading(false);
      }
    );
  };

  const chatToVoice = () => {
    const currentUrl = new URL(window.location.href);
    const currentPath = currentUrl.pathname;

    const newPath = currentPath.replace('chat', 'voice');
    const newUrl = `${newPath}${currentUrl.search}`;

    navigate(newUrl);
  };

  useEffect(() => {
    if (isMobile) {
      const chatToVoiceButton = document.getElementById('chatToVoice');
      if (chatToVoiceButton) {
        chatToVoiceButton.style.display = 'none';
      }
    }
  }, []);

  const remainingHeight = `calc(100vh - 110px)`;

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const query = useQuery();
  const agent = query.get('agent');

  useEffect(() => {
    let currAgent = '';

    if (agent) {
      switch (agent.toLowerCase()) {
        case 'shell':
          currAgent = 'shell';
          break;
        case 'spark':
          currAgent = 'spark';
          break;
        case 'spark-chicago':
          currAgent = 'chicago';
          break;
        case 'laila-sxsw':
          currAgent = 'sxsw';
          break;
        case 'laila-oracle':
          currAgent = 'oracle';
          break;
        case 'laila-ny_genai':
          currAgent = 'ny_genai';
          break;
        case 'spark-sal_france':
          currAgent = 'sal_france';
          break;
        case 'spark-houston':
          currAgent = 'houston';
          break;
        case 'innovator-glc':
          currAgent = 'glc';
          break;
        case 'spark-atlanta':
          currAgent = 'atlanta';
          break;
        case 'spark-new_york':
          currAgent = 'new_york';
          break;
        case 'robin':
          currAgent = 'robin';
          break;
        case 'spark-hess':
          currAgent = 'hess';
          break;
        case 'felix':
          currAgent = 'felix';
          break;
        case 'mair':
          currAgent = 'mair';
          break;
        case 'mair-gpt':
          currAgent = 'mair-gpt';
          break;
        case 'spark-ihub':
          currAgent = 'ihub';
          break;
        default:
          console.error('Invalid agent parameter:', agent);
          navigate('*');
          break;
      }
    } else {
      // Default to mair when no agent parameter is provided for chat
      console.log('No agent parameter provided, defaulting to mair');
      currAgent = 'mair';
      // Optionally redirect to include the agent parameter in the URL
      navigate('/chat?agent=mair', { replace: true });
    }

    if (currAgent !== '' && chatHistory.length === 2) {
      setCurrentAgent(currAgent);
      let history = initializeChatHistory(currAgent);
      setChatHistory(history);
    }
  }, [agent, navigate, setCurrentAgent, setChatHistory, chatHistory.length]);

  let promptList = [
    'What can you do?',
    'How many points will I earn on 33.2 litres of regular fuel?',
    'How can I redeem points?',
  ];

  if (currentAgent == 'mair' || currentAgent == 'mair-gpt') {
    promptList = [
      'What can you do?',
      'What is the 2025 Tech Vision?',
      'What is Innovation?',
    ];
  }

  const renderPrompts = () => {
    return promptList.map((prompt, index) => (
      <div
        key={index}
        className="Prompt flex h-12 w-full items-center justify-center rounded-xl bg-yellow-200 px-4 py-4 text-center text-[#504B78] hover:bg-yellow-600 hover:text-white "
        onClick={() => handlePresetPromptClick(prompt)}
      >
        <div className=" font-poppins text-sm font-normal leading-snug tracking-tight  ">
          {prompt}
        </div>
      </div>
    ));
  };

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-[26px] overflow-hidden bg-yellow-400">
      {/* <Header /> */}
      <div className="chat-container flex h-full w-full flex-col items-center justify-center gap-7 overflow-hidden px-4 pb-6 md:px-20 lg:min-w-[600px] lg:max-w-[1200px] lg:px-[200px]">
        <div className="flex w-full justify-end pt-8">
          <button
            type="button"
            onClick={chatToVoice}
            className="flex items-center gap-2 rounded-full border border-[#DD1D21] bg-white px-4 py-2 font-poppins text-sm font-semibold text-[#DD1D21] shadow-sm"
          >
            <ChatToVoice />
            Back to voice
          </button>
        </div>
        <div className="Chat flex w-full flex-1 flex-col overflow-y-auto ">
          <div
            className={`chat-history mb-4 flex flex-1 flex-col gap-2.5 py-1`}
            style={{ maxHeight: remainingHeight }}
          >
            {chatHistory.map((chat, index) => (
              <div
                key={index}
                className={`chat-row flex w-full items-center gap-1 ${chat.entity === 'human' ? 'flex-row-reverse' : 'flex-row'}
     }`}
              >
                {chat.entity === 'ai' && (
                  <div className="h-10 w-10">
                    {!chat.HideIcon && (
                      <div className=" h-10 w-10">
                        <img src={pecty} alt="icon" />
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={`chat-bubble flex ${chat.entity === 'ai' ? 'bg-slate-50' : 'bg-yellow-600'} ${chat.entity === 'ai' ? 'text-left' : 'text-right'} min-h-[47px]  items-center justify-center rounded-xl p-4 
     `}
                >
                  <div
                    className={`chat-bubble-text flex flex-col  ${chat.entity === 'ai' ? 'text-slate-800' : 'text-white'} font-poppins text-sm font-normal leading-[17.08px] tracking-tight`}
                  >
                    {chat.content}
                    <div className="CitationsContainer flex flex-col gap-2">
                      {chat.sources &&
                        (() => {
                          const anchorTags = [];
                          let index = 0;
                          for (const source of chat.sources) {
                            anchorTags.push(
                              <div className="CitationItem flex h-8 items-center justify-start gap-3 rounded-lg border border-gray-300">
                                <div className="CitationItemNumber flex h-full w-8 items-center justify-center rounded-bl-lg rounded-tl-lg bg-[#e7eaef] font-poppins text-xs font-semibold leading-none tracking-tight text-indigo-500">
                                  {index + 1}
                                </div>
                                <a
                                  className="font-poppins text-xs font-medium leading-none tracking-tight text-indigo-500"
                                  key={index}
                                  href={source}
                                  style={{ color: 'blue' }}
                                >
                                  {chat.display ? chat.display[index] : source}
                                </a>
                              </div>
                            );
                            index++;
                          }
                          return anchorTags;
                        })()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div
                className={`chat-row flex w-full flex-row items-center gap-1`}
              >
                <div className="h-10 w-10">
                  <div>
                    <img className=" " src={pecty} alt="icon" />
                  </div>
                </div>

                <div
                  className={`chat-bubble flex h-9 items-center justify-center rounded-xl bg-slate-50 p-4`}
                >
                  <SyncLoader color=" #f1cd55 " size={5} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
          {chatHistory.length === 2 && agent != 'glc' && (
            <div className="PromptChipContainer flex h-56 w-full flex-col items-center gap-4 rounded-2xl bg-white p-6">
              {renderPrompts()}
            </div>
          )}
          {agent == 'glc' && (
            <div className="user-input-form items-top flex w-full flex-col justify-center gap-1">
              <Editor />
            </div>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          className="user-input-form flex w-full items-center justify-center gap-1"
        >
          <input
            className="chat-input h-16 flex-1 rounded-[40px] border-none pl-4 font-fira-sans text-[#514C78] placeholder-[#908DA9] outline-none"
            type="textarea"
            value={userInput}
            onChange={handleInputChange}
            placeholder="Ask something..."
          />
          {/*
          {!isMobile && (
            <button
              type="button"
              id="chatToVoice"
              className=" flex h-16 w-16 items-center justify-center rounded-full border border-gray-300 bg-[#FFFFFF] "
              onClick={() => {
                chatToVoice();
              }}
            >
              <div className="relative">
                <ChatToVoice />
              </div>
            </button>
          )}
          */}
        </form>
      </div>
      <SwipeableDrawer
        open={isDrawerShowing}
        onClose={() => setDrawerShowing(false)}
        anchor="bottom"
        transitionDuration={1000}
        className="mt-10 h-10"
        variant="temporary"
        disableBackdropTransition={true}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            height: '90%',
            borderTopRightRadius: '10px',
            borderTopLeftRadius: '10px',
          },
        }}
      >
        <ItineraryPopUp
          onClick={() => setDrawerShowing(false)}
          qrCodeURL={`${process.env.REACT_APP_URL}/itinerary?sessionId=${sessionId}`}
          itinerary={itinerary}
        />
      </SwipeableDrawer>
    </div>
  );
};

export default ChatComponent;
